import React, { useLayoutEffect, useRef, useState } from "react";
import styles from "./ChatPropertyCard.module.scss";
import { OwnerVerifiedGradientBadge } from "../../../../../components/badge/OwnerVerifiedBadge";

/**
 * 대화 상세 내 매물 정보 카드 (01_CHAT 0.0.7)
 * 썸네일(상단) + 주소 + 거래유형 + 가격
 * 월세의 경우 depositPrice + monthlyPrice를 전달하면 보증금/월세 두 줄로 표시
 */
function ChatPropertyCard({
  className = "",
  address = "",
  transactionType = "매매",
  price = "",
  depositPrice,
  monthlyPrice,
  thumbnailUrl,
  tag,
  tagStyle,
  ownerVerified,
  onClick,
}) {
  const measureRef = useRef(null);
  const metaRef = useRef(null);
  const typeRef = useRef(null);
  const [stackPrice, setStackPrice] = useState(false);
  const isMonthly = depositPrice != null && monthlyPrice != null;

  useLayoutEffect(() => {
    if (!isMonthly) return;
    let settled = false;
    const check = () => {
      if (settled) return;
      if (!measureRef.current || !metaRef.current || !typeRef.current) return;
      const available = metaRef.current.offsetWidth - typeRef.current.offsetWidth - 4;
      const shouldStack = measureRef.current.offsetWidth > available;
      setStackPrice((prev) => {
        if (prev === shouldStack) return prev;
        // 한 번만 전환 — 진동 방지
        settled = true;
        return shouldStack;
      });
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(metaRef.current);
    return () => ro.disconnect();
  }, [isMonthly, depositPrice, monthlyPrice]);

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-name="Property card"
      role={onClick ? "button" : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {isMonthly && (
        <span ref={measureRef} className={styles.priceMeasure} aria-hidden="true">
          {depositPrice} / {monthlyPrice}
        </span>
      )}
      <div className={styles.thumbWrap}>
        <div className={styles.thumb}>
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className={styles.thumbImg} />
          ) : (
            <img
              src={icons.messager?.salePlaceholder || icons.blankImage?.sale || ""}
              alt=""
              className={styles.thumbPlaceholder}
            />
          )}
        </div>
      </div>
      <div className={styles.body}>
        {(tag || ownerVerified) && (
          <div className={styles.badgeRow}>
            {tag && (
              <span className={styles.categoryBadge} style={tagStyle}>
                {tag}
              </span>
            )}
            {ownerVerified && <OwnerVerifiedGradientBadge />}
          </div>
        )}
        <p className={styles.address}>{address}</p>
        <div className={styles.meta} ref={metaRef}>
          {stackPrice ? (
            <>
              <div className={styles.priceRow}>
                <span className={styles.transactionType} ref={typeRef}>
                  {transactionType}
                </span>
                <span className={styles.price}>{depositPrice}</span>
              </div>
              <div className={styles.priceRowSecondary}>
                <span className={styles.price}>/ {monthlyPrice}</span>
              </div>
            </>
          ) : (
            <div className={styles.priceRow}>
              <span className={styles.transactionType} ref={typeRef}>
                {transactionType}
              </span>
              <span className={styles.price}>
                {isMonthly ? `${depositPrice} / ${monthlyPrice}` : price}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatPropertyCard;
